import React, { useState } from 'react';
import Form from '../../Form/Form';
import { ResidentialCalculatorSellersNetForm } from '../../../components/Forms';
import { SellersNetTable } from '../../../components/Tables';
import { calculatePremium } from '../../../../lib/helpers';

const ResidentialCalculatorSellersNet = ({
  calculatorVariableConstants,
  calculatorConstants,
  date
}) => {
  // destructuring the calculator constants set within the WP admin.
  const {
    assessmentRecoupmentCharge,
    surveyAmendment,
    attorneyFee,
    recordingFees,
    taxCertificateFee,
    settlementClosingFee
  } = calculatorConstants;

  // defining initial input values used as the formProps
  const initialCalculationInputValues = {
    customer: '',
    propertyAddress: '',
    dateOfClosing: date,
    salesPrice: '',
    whoPaysOwnersPolicy: '',
    whoPaysSurveyAmendmentT1R: '',
    whoPaysT19_1: '',
    surveyAmendmentT1R: '',
    t19_1: ''
  };

  // defining initial table input values used as the formProps
  const initialTableInputValues = {
    payOffFirstMortgage: '',
    payOffSecondMortgage: '',
    payOffOther: '',
    percentCommission: '',
    additionalCoverage: '',
    annualTaxAmmount: '',
    unpaidHoaDues: '',
    hoaTransferFee: '',
    resaleCertificateFee: '',
    homeWarrentyFee: '',
    survey: '',
    sellerPaidBorrowerClosingCost: '',
    optionMoneyCredit: '',
    other1: '',
    other2: ''
  };

  // defining initial calulation results for Less Closing Costs component
  const initialLessClosingCostsResults = {
    commission: 0.0,
    ownersPolicyPremium: 0.0,
    assessmentRecoupmentCharge: 0.0,
    attorneyFee: Number(attorneyFee),
    recordingFees: Number(recordingFees),
    taxCertificateFee: Number(taxCertificateFee),
    settlementClosingFee: Number(settlementClosingFee),
    numberOfDaysProrate: 0,
    prorate: 0.0,
    surveyAmendmentT1RResult: 0.0,
    t19Owner: 0.0,
    salesPriceResult: ''
  };

  // Setting the inital state of the results using the useState hook
  const [lessClosingCostsResults, setLessClosingCostsResults] = useState(
    initialLessClosingCostsResults
  );

  // calculating commison
  function calculateCommission(salesPriceResult, percentCommission) {
    return (salesPriceResult * percentCommission) / 100;
  }

  // calculating number of prorate days
  function calculateNumberOfDaysProrate(dateOfClosing) {
    if (dateOfClosing === '' || dateOfClosing === null) {
      return 0;
    }
    let existingLoanOriginalDate = new Date(dateOfClosing);
    let newYear = new Date(existingLoanOriginalDate.getFullYear(), 0, 1);
    let difference = existingLoanOriginalDate - newYear;
    return Math.round(difference / 86400000) + 1;
  }

  // Determine the survey amendment
  function surveyDeletion(
    surveyAmendment,
    t19,
    ownersPolicyPremium,
    whoPaysSurveyAmendmentT1R
  ) {
    const {
      res_survey_deletion_only_percentage,
      res_survey_deletion_shared_percentage
    } = calculatorVariableConstants;
    let survey = 0.0;

    if (
      surveyAmendment === 'yes' &&
      whoPaysSurveyAmendmentT1R &&
      whoPaysSurveyAmendmentT1R !== 'buyer'
    ) {
      if (t19 == 'yes') {
        survey =
          res_survey_deletion_shared_percentage * Number(ownersPolicyPremium);
      } else {
        survey =
          res_survey_deletion_only_percentage * Number(ownersPolicyPremium);
      }

      if (survey < 20) {
        survey = 20;
      }
    }
    return survey;
  }

  function t19_1Check(t19, surveyAmendment, ownersPolicyPremium, whoPaysT19_1) {
    const {
      res_t_19_1_shared_percentage,
      res_t_19_1_only_percentage
    } = calculatorVariableConstants;
    let result = 0.0;

    if (t19 == 'yes' && whoPaysT19_1 && whoPaysT19_1 !== 'buyer') {
      if (surveyAmendment == 'yes') {
        result = res_t_19_1_shared_percentage * Number(ownersPolicyPremium);
      } else {
        result = res_t_19_1_only_percentage * Number(ownersPolicyPremium);
      }

      if (result < 50) {
        result = 50;
      }
    }

    return result;
  }

  function calculateProrateTotal(annualTaxAmmount, prorateDays) {
    return (annualTaxAmmount / 365) * prorateDays;
  }

  function totalClosingCost(values) {
    const {
      payOffFirstMortgage,
      payOffSecondMortgage,
      payOffOther,
      additionalCoverage,
      hoaTransferFee,
      homeWarrentyFee,
      survey,
      sellerPaidBorrowerClosingCost,
      optionMoneyCredit,
      unpaidHoaDues,
      resaleCertificateFee,
      other1,
      other2
    } = values;
    const {
      commission,
      ownersPolicyPremium,
      surveyAmendmentT1RResult,
      t19Owner,
      attorneyFee,
      recordingFees,
      taxCertificateFee,
      settlementClosingFee,
      prorate
    } = lessClosingCostsResults;

    return (
      Number(payOffFirstMortgage.replace(/,/g, '')) +
      Number(payOffSecondMortgage.replace(/,/g, '')) +
      Number(payOffOther.replace(/,/g, '')) +
      Number(additionalCoverage.replace(/,/g, '')) +
      Number(hoaTransferFee.replace(/,/g, '')) +
      Number(homeWarrentyFee.replace(/,/g, '')) +
      Number(survey.replace(/,/g, '')) +
      Number(sellerPaidBorrowerClosingCost.replace(/,/g, '')) +
      Number(optionMoneyCredit.replace(/,/g, '')) +
      Number(unpaidHoaDues.replace(/,/g, '')) +
      Number(other1.replace(/,/g, '')) +
      Number(other2.replace(/,/g, '')) +
      Number(resaleCertificateFee.replace(/,/g, '')) +
      Number(commission) +
      Number(ownersPolicyPremium) +
      Number(surveyAmendmentT1RResult) +
      Number(t19Owner) +
      Number(attorneyFee) +
      Number(recordingFees) +
      Number(taxCertificateFee) +
      Number(settlementClosingFee) +
      Number(prorate)
    );
  }

  function totalNetToSeller(values) {
    const { salesPriceResult } = lessClosingCostsResults;
    const closingCost = totalClosingCost(values);
    return salesPriceResult - closingCost;
  }

  // Run all the calculations and set the sate of the results accordingly
  function runCalculations() {
    const salesPriceResult = Number(salesPrice.value.replace(/,/g, ''));
    const commission = calculateCommission(
      salesPriceResult,
      percentCommission.value
    );
    const ownersPolicyPremium = calculatePremium(salesPriceResult);

    const surveyAmendmentT1RResult = surveyDeletion(
      surveyAmendmentT1R.value,
      t19_1.value,
      ownersPolicyPremium,
      typeof whoPaysSurveyAmendmentT1R === 'undefined'
        ? ''
        : whoPaysSurveyAmendmentT1R.value
    );
    const t19Owner = t19_1Check(
      t19_1.value,
      surveyAmendmentT1R.value,
      ownersPolicyPremium,
      typeof whoPaysT19_1 === 'undefined' ? '' : whoPaysT19_1.value
    );
    const numberOfDaysProrate = calculateNumberOfDaysProrate(
      dateOfClosing.value
    );
    const prorate = calculateProrateTotal(
      annualTaxAmmount.value.replace(/,/g, ''),
      numberOfDaysProrate
    );

    setLessClosingCostsResults({
      ...lessClosingCostsResults,
      assessmentRecoupmentCharge:
        !whoPaysOwnersPolicy.value || whoPaysOwnersPolicy.value === 'buyer'
          ? 0.0
          : Number(assessmentRecoupmentCharge),
      commission,
      ownersPolicyPremium:
        !whoPaysOwnersPolicy.value || whoPaysOwnersPolicy.value === 'buyer'
          ? 0.0
          : ownersPolicyPremium,
      surveyAmendmentT1RResult,
      t19Owner,
      numberOfDaysProrate,
      prorate,
      salesPriceResult
    });
  }

  function handleInputBlur(event, formProps) {
    formProps.handleInputChange(event);
    runCalculations();
  }

  // Overriding Form component handleInputChange functions
  function handleInputChange(event, formProps) {
    formProps.handleInputChange(event);
    formProps.values[event.target.name] = event.target.value;
    runCalculations(formProps.values);
  }

  return (
    <>
      <Form
        className="calc__box calc__box--tabled"
        initialCalculationInputValues={initialCalculationInputValues}
        render={({ formProps }) => (
          <ResidentialCalculatorSellersNetForm
            formProps={{
              ...formProps,
              handleInputChange: event => handleInputChange(event, formProps)
            }}
          />
        )}
      />
      <Form
        className="calc__box"
        initialCalculationInputValues={initialTableInputValues}
        render={({ formProps }) => (
          <SellersNetTable
            className="table--striped sellers-net"
            title="Less Closing Costs and Proration Charges"
            lessClosingCostsResults={lessClosingCostsResults}
            totals={{
              totalClosingCost: totalClosingCost(formProps.values),
              totalNetToSeller: totalNetToSeller(formProps.values)
            }}
            formProps={{
              ...formProps,
              handleInputChange: event => handleInputChange(event, formProps)
            }}
          />
        )}
      />
    </>
  );
};

export default ResidentialCalculatorSellersNet;
